import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { SEO } from '../../components/SEO';
import {
  MapPin, Phone, ArrowRight, Loader2, Sun, BookOpen,
  Clock, ExternalLink, Building, School, TreePine, Library
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function LocationCard({ location }) {
  const isExternal = location.external_purchase;
  return (
    <Card className="hover:shadow-lg transition-shadow" data-testid={`location-${location.id}`}>
      <CardContent className="p-5">
        <h3 className="font-bold text-lg text-gray-900 mb-1">{location.name}</h3>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(location.program_types || []).map(pt => (
            <Badge key={pt} className={pt === 'summer_camp' ? 'bg-amber-100 text-amber-700 border-amber-200 text-[10px]' : 'bg-blue-100 text-blue-700 border-blue-200 text-[10px]'}>
              {pt === 'summer_camp' ? 'Summer Camp' : 'After-School'}
            </Badge>
          ))}
          {location.eligibility_detail && (
            <Badge variant="outline" className="text-[10px]">{location.eligibility_detail}</Badge>
          )}
        </div>
        <div className="space-y-1.5 mb-4 text-sm text-gray-600">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />
            <span>{location.address}, {location.city}, {location.state} {location.zip_code}</span>
          </div>
          {location.schedule_notes && (
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />
              <div className="text-xs text-gray-500 whitespace-pre-line">{location.schedule_notes}</div>
            </div>
          )}
        </div>
        {isExternal ? (
          <a href={location.external_purchase_url} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="w-full border-purple-200 text-purple-700 hover:bg-purple-50">
              Register on Partner Site <ExternalLink className="ml-2 w-3.5 h-3.5" />
            </Button>
          </a>
        ) : (
          <Link to={`/catalog?location=${location.id}`}>
            <Button variant="outline" className="w-full">
              View Classes Here <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

export default function LocationsPage() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/locations/`)
      .then(res => setLocations(res.data.results || res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Group by category
  const homeLocation = locations.filter(l => l.location_category === 'independent');
  const schoolLocations = locations.filter(l => l.location_category === 'school');
  const libraryLocations = locations.filter(l => l.location_category === 'library');
  const parkDistricts = locations.filter(l => l.location_category === 'park_district');

  const categories = [
    { key: 'home', label: 'Home Locations', icon: Building, color: 'bg-rsc-navy', items: homeLocation, description: 'Our STEM centers — open to all families' },
    { key: 'schools', label: 'Partner Schools', icon: School, color: 'bg-blue-600', items: schoolLocations, description: 'After-school and summer camp programs at partner schools' },
    { key: 'libraries', label: 'Libraries', icon: Library, color: 'bg-purple-600', items: libraryLocations, description: 'Half-day STEM camps at local libraries' },
    { key: 'parks', label: 'Park Districts', icon: TreePine, color: 'bg-emerald-600', items: parkDistricts, description: 'Summer camps through local park districts — register on their sites' },
  ];

  return (
    <div data-testid="locations-page">
      <SEO title="Our Locations" description="Find a Ready Set STEM center near you. Summer camp and after-school STEM programs across Aurora, Naperville, and Fox Valley IL." />

      {/* Hero */}
      <section className="relative py-14 bg-gradient-to-br from-rsc-navy via-[#1a3a6d] to-[#0f2847] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(251,191,36,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.2) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="container-app relative z-10">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3">
            Our <span className="text-rsc-yellow">Locations</span>
          </h1>
          <p className="text-lg text-white/70 max-w-xl">
            Find the nearest Ready Set STEM location for camps, classes, and programs
          </p>
          <div className="flex gap-6 mt-6">
            {categories.map(cat => (
              <div key={cat.key} className="text-center">
                <p className="text-2xl font-bold text-rsc-yellow">{cat.items.length}</p>
                <p className="text-xs text-white/40">{cat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations by Category */}
      <section className="py-8 bg-gray-50 min-h-[40vh]">
        <div className="container-app">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-rsc-navy" />
            </div>
          ) : (
            <div className="space-y-10">
              {categories.map(cat => {
                if (cat.items.length === 0) return null;
                const Icon = cat.icon;
                return (
                  <div key={cat.key} data-testid={`category-${cat.key}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-xl ${cat.color} flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">{cat.label}</h2>
                        <p className="text-sm text-gray-500">{cat.description}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {cat.items.map(loc => (
                        <LocationCard key={loc.id} location={loc} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-12 bg-white">
        <div className="container-app text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Questions?</h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">We're here to help you find the perfect program</p>
          <Link to="/contact">
            <Button className="bg-rsc-navy text-white px-8" data-testid="contact-btn">
              Contact Us <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
